import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import type {
  OptionsEntityFieldConfiguration,
  UserDefinedOptionsEntityFieldConfiguration,
} from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import { getSelectOptionsFromDictionary } from "../../../utilities/dictionariesHelper";

interface Props {
  value: unknown;
  fieldConfiguration: OptionsEntityFieldConfiguration | UserDefinedOptionsEntityFieldConfiguration;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  hasError?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
}

const SelectFieldEdit = ({ value, fieldConfiguration, onChange, disabled, hasError, helperText, sx }: Props) => {
  const { dictionaries } = useClientContext();

  const options = useMemo(
    () =>
      fieldConfiguration.$type === "UserDefinedOptionsSelect"
        ? fieldConfiguration.userDefinedOptions
        : getSelectOptionsFromDictionary(fieldConfiguration.fieldType, dictionaries),
    [fieldConfiguration, dictionaries]
  );

  const selectedOption = options.find((o) => o.value === value) ?? null;

  return (
    <Autocomplete
      disabled={disabled}
      fullWidth
      openOnFocus
      options={options}
      value={selectedOption}
      onChange={(_, option) => onChange(option?.value ?? undefined)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={(params) => <TextField {...params} error={hasError} helperText={helperText} />}
      sx={sx}
    />
  );
};

export default SelectFieldEdit;
